import React from 'react';
import { Box, Typography, Tooltip, IconButton, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PrimaryButton } from '../button';
import { PrimaryTextField } from '../input';
import PropTypes from 'prop-types';

const PageHeader = ({
  title,
  rightContent,
  connectorIcon,
  connectorIconClick,
  createButtonText = 'Create New',
  createButtonClick,
  showConnectorIcon = true,
  showSearchIcon = false,
  searchIconClick,
  searchOpen = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search…',
}) => {
  const navigate = useNavigate();

  const handleConnectorClick = () => {
    if (connectorIconClick) {
      connectorIconClick();
    } else {
      navigate('/connector');
    }
  };

  return (
    <Box
      sx={{
        bgcolor: '#F1F5FF',
        py: { xs: 1.5, sm: 2, md: 2.5 },
        px: { xs: 1, sm: 2, md: 3.5 },
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: 2 },
        flexWrap: 'wrap',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontWeight: 700,
          fontStyle: 'normal',
          fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
          lineHeight: '100%',
          color: '#383838',
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2, md: 2.5 },
          position: 'relative',
        }}
      >
        {showSearchIcon && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnimatePresence initial={false}>
              {searchOpen ? (
                <motion.div
                  key="searchbar"
                  initial={{ width: 0, opacity: 0, x: 30 }}
                  animate={{ width: 280, opacity: 1, x: 0 }}
                  exit={{ width: 0, opacity: 0, x: 30 }}
                  transition={{ type: 'tween', duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden', display: 'flex', alignItems: 'center' }}
                >
                  <PrimaryTextField
                    autoFocus
                    value={searchValue}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    placeholder={searchPlaceholder}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              onSearchChange?.('');
                              searchIconClick?.();
                            }}
                            edge="end"
                            sx={{ p: 0.5 }}
                          >
                            <CloseRounded sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 280,
                      '& .MuiOutlinedInput-root': {
                        height: 36,
                        borderRadius: '4px',
                        bgcolor: '#F9FAFB',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </motion.div>
              ) : (
                <Tooltip title="Search">
                  <IconButton
                    onClick={searchIconClick}
                    size="small"
                    sx={{
                      p: 0.75,
                      color: '#383838',
                      '&:hover': {
                        bgcolor: '#E5E7EB',
                      },
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </AnimatePresence>
          </Box>
        )}
        {showConnectorIcon && connectorIcon && (
          <Tooltip title="Show Connectors">
            <Box
              onClick={handleConnectorClick}
              sx={{
                borderRadius: '999px',
                p: 0.75,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition:
                  'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease, background-color 180ms ease',
                backgroundColor: '#DDF3FA',
                '&:hover': {
                  backgroundColor: '#F4FBFD',
                  transform: 'translateY(-1px) scale(1.05)',
                  boxShadow: '0 8px 18px rgba(47, 93, 99, 0.35)',
                },
                '&:active': {
                  transform: 'scale(0.97)',
                  boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
                },
              }}
            >
              <Box
                component="img"
                src={connectorIcon}
                alt="Connectors"
                sx={{ width: { xs: 24, sm: 26, md: 28 }, height: { xs: 24, sm: 26, md: 28 } }}
              />
            </Box>
          </Tooltip>
        )}

        {rightContent && rightContent}

        {createButtonClick && (
          <motion.div whileTap={{ scale: 0.98 }}>
            <PrimaryButton
              variant="contained"
              onClick={createButtonClick}
              disableElevation
              data-tour="create-connection"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                px: { xs: 1.5, sm: 1.75, md: 2 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
              }}
            >
              {createButtonText}
            </PrimaryButton>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  rightContent: PropTypes.node,
  connectorIcon: PropTypes.string,
  connectorIconClick: PropTypes.func,
  createButtonText: PropTypes.string,
  createButtonClick: PropTypes.func,
  showConnectorIcon: PropTypes.bool,
  showSearchIcon: PropTypes.bool,
  searchIconClick: PropTypes.func,
  searchOpen: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
};

export default PageHeader;
